import {
  InnerContentItem,
  OuterContentItem,
  PrimaryTag,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { formatDate } from "@certn/utils";
import { Trans, useTranslation } from "react-i18next";

import { convertToTitleCase } from "./utils";
import { useCountryName } from "../../../../components/AddressInput/hooks";

export function EducationSummaryField({
  field,
}: {
  field: components["schemas"]["EducationCheckField"];
}) {
  const { t } = useTranslation();
  const getCountryName = useCountryName();

  return (
    <>
      {field.value.length === 0 && (
        <InnerContentItem
          textStyle="global/body-secondary"
          textColor="text-weak"
          testID="education-summary-no-education-provided"
        >
          <Trans>None provided</Trans>
        </InnerContentItem>
      )}

      {field.value.map((education, index) => (
        <OuterContentItem
          title={`${education.degree}`}
          subtitle={convertToTitleCase(education.institution)}
          key={education.id}
          tag={
            education.highest_level ? (
              <PrimaryTag type="subtle">
                <Trans>Highest level</Trans>
              </PrimaryTag>
            ) : (
              education.current && (
                <PrimaryTag type="subtle">
                  <Trans>In progress</Trans>
                </PrimaryTag>
              )
            )
          }
          testID={`education-summary-education-${index + 1}`}
        >
          <InnerContentItem
            label={t("Degree title")}
            testID={`education-summary-education-${index + 1}-degree`}
          >
            {education.degree}
          </InnerContentItem>
          <InnerContentItem
            label={t("Institution")}
            testID={`education-summary-education-${index + 1}-institution`}
          >
            {convertToTitleCase(education.institution)}
          </InnerContentItem>
          <InnerContentItem
            label={t("Start date")}
            testID={`education-summary-education-${index + 1}-start-date`}
          >
            {formatDate(education.start_date, "MMMM yyyy")}
          </InnerContentItem>
          {education.end_date ? (
            <InnerContentItem
              label={t("Date awarded")}
              testID={`education-summary-education-${index + 1}-end-date`}
            >
              {formatDate(education.end_date, "MMMM yyyy")}
            </InnerContentItem>
          ) : (
            <InnerContentItem
              label={t("Date awarded")}
              testID={`education-summary-education-${index + 1}-in-progress`}
            >
              <Trans>In progress</Trans>
            </InnerContentItem>
          )}
          <InnerContentItem
            label={t("Address")}
            testID={`education-summary-education-${index + 1}-address`}
          >
            {getCountryName(education.address?.country ?? "")}
            {", "}
            {education.address?.province_state}
          </InnerContentItem>

          {field.require_highest_level && (
            <InnerContentItem
              label={t("Highest level of Education")}
              testID={`education-summary-education-${index + 1}-highest-level`}
            >
              {education.highest_level ? <Trans>Yes</Trans> : <Trans>No</Trans>}
            </InnerContentItem>
          )}
        </OuterContentItem>
      ))}
    </>
  );
}
